import { Footer } from "@/components/Footer";
import { PageTopBanner } from "@/components/PageTopBanner";
import { CTABanner } from "@/components/CTABanner";
import { SEO } from "@/components/SEO";
import profileImage from "@/assets/khaled-profile.jpg";
import Lottie from "lottie-react";
import goldCupAnimation from "@/assets/gold-cup-award.json";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { useLanguage } from "@/contexts/LanguageContext";

const About = () => {
  const { visibleItems, itemRefs } = useScrollAnimation();
  const { t } = useLanguage();

  const sections = [
    { title: "Khaled Kasmou", type: "header" },
    { title: "Background", type: "section" },
    { title: "What I Do", type: "section" },
    { title: "Beyond Design", type: "section" },
    { title: "Let's Connect", type: "section" }
  ];

  const faqs = [
    { q: t("about.faq.q1"), a: t("about.faq.a1") },
    { q: t("about.faq.q2"), a: t("about.faq.a2") },
    { q: t("about.faq.q3"), a: t("about.faq.a3") },
    { q: t("about.faq.q4"), a: t("about.faq.a4") },
  ];

  return (
    <div className="min-h-screen pt-20">
      <SEO
        title="About Khaled Kasmou | Product & UX/UI Designer in Sofia"
        description="Learn about Khaled Kasmou — a product and UX/UI designer in Sofia, Bulgaria, working across product strategy, UX architecture, interface design, and design systems."
        path="/about"
        type="profile"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "ProfilePage",
            mainEntity: {
              "@type": "Person",
              name: "Khaled Kasmou",
              url: "https://khaledkasmou.me/about",
              jobTitle: "Product & UX/UI Designer",
              address: {
                "@type": "PostalAddress",
                addressLocality: "Sofia",
                addressCountry: "BG",
              },
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://khaledkasmou.me/" },
              { "@type": "ListItem", position: 2, name: "About", item: "https://khaledkasmou.me/about" },
            ],
          },
        ]}
      />
      <div className="relative">
        <PageTopBanner
          titleKey="about.banner.title"
          subtitleKey="about.banner.subtitle"
          title=""
        />
        {/* Decorative animation */}
        <div className="absolute -bottom-16 left-1/2 -translate-x-1/2 w-32 h-32 md:w-40 md:h-40 animate-fade-in z-10">
          <div aria-hidden="true"><Lottie animationData={goldCupAnimation} loop={true} /></div>
        </div>
      </div>
      <main>
      <article>
      <section className="relative max-w-6xl mx-auto px-6 py-16 md:py-24">
        
        <div className="grid md:grid-cols-2 gap-12 items-start">
          {/* Profile Image with Artistic Effect */}
          <div className="animate-fade-in-slow order-2 md:order-1 sticky top-24">
            <div className="relative group">
              {/* Gradient Background Blob */}
              <div className="absolute -inset-4 bg-gradient-to-br from-primary/30 via-accent/20 to-primary/20 rounded-3xl blur-2xl opacity-60 group-hover:opacity-80 transition-opacity duration-500" />
              
              {/* Image Container */}
              <div className="relative overflow-hidden rounded-2xl shadow-card hover:shadow-hover transition-smooth">
                <img 
                  src={profileImage} 
                  alt="Khaled Kasmou — product and UX/UI designer"
                  className="w-full h-auto object-cover transform group-hover:scale-105 transition-transform duration-700"
                />
                {/* Overlay Gradient on Hover */}
                <div className="absolute inset-0 bg-gradient-to-t from-background/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>

              {/* Decorative Border */}
              <div className="absolute -bottom-6 -right-6 w-32 h-32 border-2 border-primary/20 rounded-2xl -z-10" />
              <div className="absolute -top-6 -left-6 w-24 h-24 border-2 border-accent/20 rounded-2xl -z-10" />
            </div>
          </div>

          {/* Content */}
          <div className="space-y-8 text-lg leading-relaxed text-foreground order-1 md:order-2">
            <div
              ref={(el) => (itemRefs.current[0] = el)}
              className={`space-y-4 transition-all duration-700 ${
                visibleItems.has(0)
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
            >
              <h2 className="text-2xl font-semibold">{t("about.who.title")}</h2>
              <p className="text-muted-foreground">{t("about.who.body")}</p>
            </div>

            <div
              ref={(el) => (itemRefs.current[1] = el)}
              className={`space-y-4 pt-8 transition-all duration-700 ${
                visibleItems.has(1)
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
            >
              <h3 className="text-xl font-semibold">{t("about.background.title")}</h3>
              <p className="text-muted-foreground">{t("about.background.p1")}</p>
              <p className="text-muted-foreground">{t("about.background.p2")}</p>
            </div>

            <div
              ref={(el) => (itemRefs.current[2] = el)}
              className={`space-y-4 pt-8 transition-all duration-700 ${
                visibleItems.has(2)
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
            >
              <h3 className="text-xl font-semibold">{t("about.what.title")}</h3>
              <p className="text-muted-foreground">{t("about.what.p1")}</p>
              <p className="text-muted-foreground">{t("about.what.p2")}</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1 pt-2">
                <li>{t("about.what.li1")}</li>
                <li>{t("about.what.li2")}</li>
                <li>{t("about.what.li3")}</li>
                <li>{t("about.what.li4")}</li>
                <li>{t("about.what.li5")}</li>
              </ul>
            </div>

            <div
              ref={(el) => (itemRefs.current[3] = el)}
              className={`space-y-4 pt-8 transition-all duration-700 ${
                visibleItems.has(3)
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
            >
              <h3 className="text-xl font-semibold">{t("about.beyond.title")}</h3>
              <p className="text-muted-foreground">{t("about.beyond.body")}</p>
            </div>

            <div
              ref={(el) => (itemRefs.current[4] = el)}
              className={`pt-12 space-y-2 transition-all duration-700 ${
                visibleItems.has(4)
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
            >
              <h3 className="text-xl font-semibold mb-4">{t("about.connect.title")}</h3>
              <div className="flex flex-col gap-2 text-muted-foreground">
                <a href="mailto:khaledkasmou@gmail.com" className="hover:text-foreground transition-colors">
                  {t("about.connect.email")}
                </a>
                <a href="https://www.linkedin.com/in/khaledkasmou/" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">
                  LinkedIn
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section — AEO Position Zero */}
      <section className="max-w-4xl mx-auto px-6 pb-16 md:pb-24" aria-labelledby="faq-heading">
        <h2 id="faq-heading" className="text-3xl font-semibold mb-8">{t("about.faq.title")}</h2>
        <dl className="space-y-6">
          {faqs.map((f) => (
            <div key={f.q} className="border-b border-border pb-6">
              <dt className="text-lg font-semibold text-foreground mb-2">{f.q}</dt>
              <dd className="text-muted-foreground leading-relaxed">{f.a}</dd>
            </div>
          ))}
        </dl>
      </section>
      </article>
      </main>

      <CTABanner />
      <Footer />
    </div>
  );
};

export default About;
